import React, { useState } from 'react';
import { getAuditLog } from '../services/api';
import { formatDateTime } from '../utils/helpers';
import { Search, Clock } from 'lucide-react';

const ENTITY_TYPES = ['', 'payment_term', 'installment', 'invoice', 'milestone', 'subscription', 'unbilled_revenue', 'purchase_order'];

export default function AuditLogPage() {
  const [entityId, setEntityId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!entityId.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await getAuditLog({ entity_id: entityId.trim(), entity_type: entityType || undefined });
      setLogs(res.data.audit_log || []);
    } catch (err) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const eventColor = (eventType) => {
    if (eventType.includes('CREATED')) return 'bg-blue-100 text-blue-700';
    if (eventType.includes('APPROVED') || eventType.includes('ACCEPTED') || eventType.includes('PAID')) return 'bg-green-100 text-green-700';
    if (eventType.includes('REJECT') || eventType.includes('CANCEL') || eventType.includes('OVERDUE')) return 'bg-red-100 text-red-700';
    if (eventType.includes('AMEND') || eventType.includes('UPDATED')) return 'bg-yellow-100 text-yellow-700';
    if (eventType.includes('TRIGGER')) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Clock size={22} className="text-gray-500" />
        <h2 className="text-xl font-semibold">Audit Log</h2>
      </div>

      <div className="card p-5">
        <form onSubmit={handleSearch} className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="label">Entity ID (UUID)</label>
            <input className="input font-mono" value={entityId} onChange={e => setEntityId(e.target.value)}
              placeholder="e.g. 550e8400-e29b-41d4-a716-446655440000" />
          </div>
          <div>
            <label className="label">Entity Type</label>
            <select className="select" value={entityType} onChange={e => setEntityType(e.target.value)}>
              {ENTITY_TYPES.map(t => <option key={t} value={t}>{t || 'All Types'}</option>)}
            </select>
          </div>
          <button type="submit" disabled={!entityId.trim() || loading} className="btn-primary">
            <Search size={16} /> {loading ? 'Searching…' : 'Search'}
          </button>
        </form>
        <p className="text-xs text-gray-400 mt-2">Paste any entity ID (SO, Term, Installment, Invoice, Milestone, etc.) to view its full immutable audit trail.</p>
      </div>

      {searched && (
        <div className="card overflow-hidden">
          {logs.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <Clock size={32} className="mx-auto mb-2 opacity-40" />
              <p>No audit entries found for this entity</p>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b text-sm text-gray-500">{logs.length} audit entries</div>
              <div className="divide-y divide-gray-100">
                {logs.map(log => (
                  <div key={log.audit_id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`badge ${eventColor(log.event_type)}`}>{log.event_type.replace(/_/g,' ')}</span>
                          <span className="text-xs text-gray-500 font-medium">{log.entity_type}</span>
                          <span className="text-xs text-gray-400 font-mono">{log.entity_id?.slice(0,8)}…</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>By: <strong>{log.user_name || 'System'}</strong></span>
                          {log.notes && <span>Note: {log.notes}</span>}
                        </div>
                        {(log.before_state || log.after_state) && (
                          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {log.before_state && (
                              <div className="bg-red-50 rounded p-2">
                                <div className="text-xs font-semibold text-red-600 mb-1">Before</div>
                                <pre className="text-xs text-red-800 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(log.before_state, null, 2)}</pre>
                              </div>
                            )}
                            {log.after_state && (
                              <div className="bg-green-50 rounded p-2">
                                <div className="text-xs font-semibold text-green-600 mb-1">After</div>
                                <pre className="text-xs text-green-800 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(log.after_state, null, 2)}</pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(log.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
